import { showDeleteConfirmation } from '@modules/common';
import { switchMap } from 'rxjs/operators';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { handleMessage, requestMessage } from '@business/messages';
import { FormId } from '@business/entities/form';
import { pipeIf } from '@core/utils/rxjs/operators';
import { ManagementFormTemplateAPI } from '@business/api/managementFormTemplate';
import { ManagementFormTemplateResponse, CompactManagementFormTemplateResponse } from '@business/entities/managementFormTemplate';
import { ManagementFormTemplateData } from '@modules/Forms/AddEditFormModal/AddEditFormModal';

export class ManagementFormTemplateBloc extends BaseBloc {
  private readonly formRepo = new Repository<ManagementFormTemplateResponse | CompactManagementFormTemplateResponse>({
    getItemId: form => form.id,
  });

  readonly forms$ = this.formRepo.items$;
  readonly selectForm = this.formRepo.selectItem;

  onReset() {
    this.formRepo.reset();
  }

  fetchForms = () => {
    return ManagementFormTemplateAPI.fetchForms().pipe(this.formRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_form_error') }));
  };

  fetchForm = (id: FormId) => {
    return ManagementFormTemplateAPI.fetchForm(id).pipe(
      this.formRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_form_error') }),
    );
  };

  createForm = (data: ManagementFormTemplateData) => {
    return ManagementFormTemplateAPI.createForm(data).pipe(
      this.formRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_form'),
      }),
    );
  };

  updateForm = (id: FormId, data: ManagementFormTemplateData) => {
    return ManagementFormTemplateAPI.updateForm(id, data).pipe(
      this.formRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_form'),
      }),
    );
  };

  deleteForm = (id: FormId) => {
    return showDeleteConfirmation('Delete Form', 'Do you really want to remove this form?').pipe(
      switchMap(() => ManagementFormTemplateAPI.deleteForm(id)),
      this.formRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_form'),
      }),
    );
  };

  duplicateForm = (id: FormId, form: ManagementFormTemplateData) => {
    return ManagementFormTemplateAPI.duplicateForm(id, form).pipe(
      this.formRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('clone_form'),
      }),
    );
  };

  saveFormFields = (id: FormId, data: Pick<ManagementFormTemplateResponse, 'fields'>, message = true) => {
    return ManagementFormTemplateAPI.saveFormFields(id, data).pipe(
      this.formRepo.ops.upsertOne(item => ({ item })),
      pipeIf(
        message &&
          handleMessage({
            type: requestMessage('save_form_fields'),
          }),
      ),
    );
  };

  deleteFormShare = (id: string) => {
    return ManagementFormTemplateAPI.deleteFormShare(id);
  };
}
